/**
 * React Router v5 → v6 호환성 레이어
 * 
 * 기존 useHistory 코드가 동작하도록 useNavigate를 래핑합니다.
 * 점진적 마이그레이션을 위해 사용합니다.
 * 
 * Usage:
 * - 기존: import { useHistory } from 'react-router-dom';
 * - 변경: import { useHistory } from '../hooks/useRouterCompat';
 */

import { useNavigate, useLocation, NavigateFunction } from 'react-router-dom';
import { useMemo } from 'react';

interface HistoryCompatible {
    push: (path: string, state?: any) => void;
    replace: (path: string, state?: any) => void;
    goBack: () => void;
    goForward: () => void;
    go: (delta: number) => void;
    listen: (callback: (location: any, action: string) => void) => () => void;
    location: {
        pathname: string;
        search: string;
        hash: string;
        state: any;
        key: string;
    };
}

/**
 * useHistory 호환 훅
 * react-router v5의 useHistory API를 v6의 useNavigate로 에뮬레이션합니다.
 */
export function useHistory<T = unknown>(): HistoryCompatible {
    const navigate = useNavigate();
    const location = useLocation();

    return useMemo(() => ({
        push: (path: string, state?: any) => {
            navigate(path, { state });
        },
        replace: (path: string, state?: any) => {
            navigate(path, { replace: true, state });
        },
        goBack: () => {
            navigate(-1);
        },
        goForward: () => {
            navigate(1);
        },
        go: (delta: number) => {
            navigate(delta);
        },
        listen: (callback: (location: any, action: string) => void) => {
            // v6에서는 listen이 없으므로 빈 함수 반환
            // 실제로 listen이 필요한 코드는 useLocation + useEffect로 마이그레이션 필요
            console.warn('[useHistory compat] listen() is deprecated in v6. Use useLocation + useEffect instead.');
            return () => { };
        },
        location: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            state: location.state,
            key: location.key,
        }
    }), [navigate, location]);
}

/**
 * useNavigate 래퍼 (새 코드용)
 */
export { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
